import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import ModernRichTextEditor from "@/components/ModernRichTextEditor";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// E-Mail Platzhalter für Vorlagen
const TEMPLATE_PLACEHOLDERS = [
  { key: "vorname", label: "Vorname", description: "Vorname des Kindes" },
  { key: "nachname", label: "Nachname", description: "Nachname des Kindes" },
  { key: "gruppe", label: "Gruppe", description: "Name der Gruppe" },
];

export function MailTemplateManager({ authToken, onTemplateSelected }) {
  const { toast } = useToast();
  const editorRef = useRef(null);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // Template to confirm deletion

  const [mailEditorColors, setMailEditorColors] = useState([
    "#000000",
    "#1f2937",
    "#0f766e",
    "#2563eb",
    "#b91c1c",
  ]);

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    subject: "",
    html: "",
  });

  async function loadEditorSettings() {
    if (!authToken) return;
    try {
      const res = await axios.get(`${API_BASE}/api/mail-editor-settings`, authHeaders());
      if (Array.isArray(res.data?.colors)) {
        setMailEditorColors(res.data.colors.slice(0, 5));
      }
    } catch (e) {
      console.error(e);
    }
  }

  function authHeaders() {
    return authToken
      ? { headers: { Authorization: `Bearer ${authToken}` } }
      : {};
  }

  async function loadTemplates() {
    if (!authToken) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/mail-templates`, authHeaders());
      setTemplates(res.data || []);
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Laden der Mail-Vorlagen", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTemplates();
    loadEditorSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  function resetForm() {
    setEditingId(null);
    setForm({ name: "", subject: "", html: "" });
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!authToken) return;
    
    const payload = {
      name: form.name,
      subject: form.subject,
      html: form.html,
    };

    if (!payload.name || !payload.subject) {
      toast({ title: "Bitte Name und Betreff ausfüllen", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        const res = await axios.put(`${API_BASE}/api/mail-templates/${editingId}`, payload, authHeaders());
        toast({ title: "Vorlage aktualisiert" });
        setTemplates((prev) => prev.map((t) => (t.id === editingId ? res.data : t)));
      } else {
        const res = await axios.post(`${API_BASE}/api/mail-templates`, payload, authHeaders());
        toast({ title: "Vorlage gespeichert" });
        setTemplates((prev) => [...prev, res.data]);
      }
      resetForm();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Vorlage konnte nicht gespeichert werden.";
      toast({ title: "Fehler", description: msg, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  function startEdit(tpl) {
    setEditingId(tpl.id);
    setForm({
      name: tpl.name || "",
      subject: tpl.subject || "",
      html: tpl.html || "",
    });
  }

  async function handleDelete(tpl) {
    if (!authToken) return;
    try {
      await axios.delete(`${API_BASE}/api/mail-templates/${tpl.id}`, authHeaders());
      setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
      if (editingId === tpl.id) resetForm();
      setDeleteConfirm(null);
      toast({ title: "Vorlage gelöscht" });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Vorlage konnte nicht gelöscht werden.";
      toast({ title: "Fehler", description: msg, variant: "destructive" });
    }
  }

  function handleUseTemplate(tpl) {
    if (onTemplateSelected) {
      onTemplateSelected({ subject: tpl.subject, html: tpl.html, id: tpl.id });
    }
  }

  return (
    <Card className="border-slate-200 bg-white/90" data-testid="mail-template-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-slate-900">Mail-Vorlagen</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Lösch-Bestätigung Dialog */}
        {deleteConfirm && (
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-rose-100">
                <svg className="h-5 w-5 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-rose-900">Vorlage löschen?</h4>
                <p className="mt-1 text-xs text-rose-700">
                  Möchten Sie die Vorlage <strong>"{deleteConfirm.name}"</strong> wirklich unwiderruflich löschen?
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(null)}
                    className="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(deleteConfirm)}
                    className="rounded-full bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-500"
                  >
                    Ja, löschen
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Vorlagen-Liste */}
        <div className="rounded-xl bg-slate-50 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600">
              {loading ? "Lädt..." : `${templates.length} Vorlagen`}
            </span>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="text-xs text-violet-600 hover:text-violet-700"
              >
                + Neue Vorlage
              </button>
            )}
          </div>
          
          {templates.length === 0 ? (
            <p className="text-xs text-slate-500 py-2">Noch keine Vorlagen angelegt.</p>
          ) : (
            <div className="space-y-1.5">
              {templates.map((tpl) => (
                <div
                  key={tpl.id}
                  className={`flex items-center justify-between rounded-lg bg-white p-2.5 shadow-sm transition-all ${
                    editingId === tpl.id ? "ring-2 ring-violet-400" : ""
                  }`}
                  data-testid={`mail-template-row-${tpl.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-slate-900 truncate">{tpl.name}</div>
                    <div className="text-xs text-slate-500 truncate">{tpl.subject}</div>
                  </div>
                  <div className="flex items-center gap-1.5 ml-2">
                    <button
                      type="button"
                      onClick={() => startEdit(tpl)}
                      className="rounded-lg px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-100"
                      data-testid={`mail-template-edit-${tpl.id}`}
                    >
                      Bearbeiten
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUseTemplate(tpl)}
                      className="rounded-lg bg-violet-100 px-2.5 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-200"
                      data-testid={`mail-template-use-${tpl.id}`}
                    >
                      Übernehmen
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteConfirm(tpl)}
                      className="rounded-lg px-2.5 py-1.5 text-xs text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                      data-testid={`mail-template-delete-${tpl.id}`}
                      title="Vorlage löschen"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Formular */}
        <form className="space-y-3" onSubmit={handleSave} data-testid="mail-template-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-slate-500">Name der Vorlage</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="z.B. Info vor Sommerferien"
                className="w-full rounded-lg border-0 bg-slate-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">Betreff</label>
              <input
                type="text"
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                placeholder="z.B. Wichtige Info zum Schwimmkurs"
                className="w-full rounded-lg border-0 bg-slate-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs text-slate-500">Nachricht mit Platzhaltern</label>
            <ModernRichTextEditor
              ref={editorRef}
              value={form.html}
              onChange={(html) => setForm((f) => ({ ...f, html }))}
              placeholder="Text der Mail-Vorlage eingeben..."
              colors={mailEditorColors}
              placeholders={TEMPLATE_PLACEHOLDERS}
              showPlaceholders={true}
              minHeight="180px"
            />
          </div>

          <div className="flex justify-end gap-2">
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="rounded-full bg-slate-100 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-200"
              >
                Abbrechen
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              className="rounded-full bg-violet-600 px-4 py-2 text-xs font-medium text-white hover:bg-violet-500 disabled:opacity-50"
              data-testid="mail-template-save-button"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Speichern...
                </span>
              ) : editingId ? (
                "Vorlage aktualisieren"
              ) : (
                "Vorlage speichern"
              )}
            </button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
